import React from 'react';
import PrivacyModal from './PrivacyModal';
import heroPhoto from '../resources/entrepreneurs-hero.webp';

const h = React.createElement;

function Icon({ name, className = '' }) {
  const paths = {
    clock: [h('circle', { cx: 12, cy: 12, r: 9, key: 'c' }), h('path', { d: 'M12 7v5l3 2', key: 'p' })],
    assess: [h('path', { d: 'M14 3H5v18h14V8l-5-5Z M14 3v5h5 M8 12h5 M8 16h3', key: 'p' }), h('circle', { cx: 17, cy: 17, r: 4, key: 'c' }), h('path', { d: 'm15 17 1.5 1.5L19 16', key: 't' })],
    discover: [h('path', { d: 'M4 13h4v8H4z M10 8h4v13h-4z M16 3h4v18h-4z', key: 'p' })],
    grow: [h('path', { d: 'M12 21v-9 M12 15C5 15 3 11 3 5c6 0 9 3 9 8 M12 12c0-6 3-9 9-9 0 6-3 9-9 9 M7 9l5 6 M17 7l-5 5', key: 'p' })],
    people: [h('circle', { cx: 12, cy: 7, r: 3, key: 'c1' }), h('path', { d: 'M6 21v-3a6 6 0 0 1 12 0v3 M4 7a2 2 0 1 0 0 4 M20 7a2 2 0 1 1 0 4 M2 20v-3a4 4 0 0 1 2-3 M22 20v-3a4 4 0 0 0-2-3', key: 'p' })],
    light: [h('path', { d: 'M8 15a7 7 0 1 1 8 0l-1 3H9l-1-3Z M9 21h6 M12 1V0 M3 5 1 4 M21 5l2-1', key: 'p' })],
    arrow: [h('path', { d: 'M4 12h15 M13 6l6 6-6 6', key: 'p' })],
  };
  return h('svg', { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.65,
    strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true, className }, paths[name] || paths.arrow);
}

const steps = [
  { number: '01', icon: 'assess', title: 'Assess', text: 'Explore key components of your entrepreneurial mindset through a structured questionnaire.' },
  { number: '02', icon: 'discover', title: 'Discover', text: 'Gain a clearer understanding of your strengths, opportunities and areas for development.' },
  { number: '03', icon: 'grow', title: 'Improve', text: 'Use your insights to support your learning, career planning or entrepreneurial journey.' },
];

const facts = [
  { icon: 'people', title: 'For everyone', text: 'Students, professionals and founders' },
  { icon: 'clock', title: '15-20 minutes', text: 'A little time for a clearer perspective' },
  { icon: 'discover', title: 'Research-based', text: 'Informed by entrepreneurship research' },
  { icon: 'light', title: 'Made for reflection', text: 'Turn understanding into your next step' },
];

export default function HomeGrid({ isLoggedIn }) {
  const start = (label, extraClass = '') => h(PrivacyModal, {
    isLoggedIn, buttonLabel: label, buttonClassName: 'emap-button ' + extraClass, showArrow: true,
  });

  return h(React.Fragment, null,
    h('div', { className: 'emap-main' },
      h('section', { className: 'emap-hero emap-container', 'aria-labelledby': 'emap-headline' },
        h('div', { className: 'emap-hero-copy' },
          h('p', { className: 'emap-eyebrow' }, h('span', { className: 'emap-eyebrow-line' }), 'Explore. Assess. Grow.'),
          h('h1', { id: 'emap-headline', className: 'emap-headline' },
            'Explore your ', h('span', null, 'entrepreneurial'), ' mindset.'),
          h('p', { className: 'emap-intro' },
            'Understand your entrepreneurial mindset. Discover your strengths and areas for growth through a research-based assessment.'),
          h('div', { className: 'emap-hero-actions' },
            start('Start Assessment'),
            h('a', { className: 'emap-text-link', href: '#how-it-works' }, 'How it works', h(Icon, { name: 'arrow' }))),
          h('p', { className: 'emap-time' }, h(Icon, { name: 'clock' }), 'Takes approximately 15-20 minutes')),
        h('div', { className: 'emap-hero-visual' },
          h('div', { className: 'emap-photo-wrap' },
            h('img', { src: heroPhoto, className: 'emap-hero-photo',
              alt: 'Three adults exploring ideas together around a laptop.',
              width: 1448, height: 1086, fetchpriority: 'high' })),
          h('div', { className: 'emap-idea-note', 'aria-hidden': true },
            'Ideas today.', h('br'), 'Impact tomorrow.', h('span')),
          h('div', { className: 'emap-reflection-note' },
            h('span', { className: 'emap-note-mark', 'aria-hidden': true }, '↗'),
            h('p', null, 'A clearer picture.', h('br'), h('strong', null, 'A more informed next step.'))),
          h('div', { className: 'emap-dot-pattern', 'aria-hidden': true }))),
      h('section', { id: 'how-it-works', className: 'emap-process emap-container', 'aria-labelledby': 'emap-process-title' },
        h('div', { className: 'emap-section-heading' },
          h('h2', { id: 'emap-process-title' }, 'A little reflection. ', h('span', null, 'A new perspective.')),
          h('p', null, 'Three steps to understanding your mindset.')),
        h('div', { className: 'emap-steps' },
          steps.map(step => h('article', { className: 'emap-step', key: step.number },
            h('div', { className: 'emap-step-icon emap-step-icon--' + step.icon }, h(Icon, { name: step.icon })),
            h('div', { className: 'emap-step-copy' }, h('h3', null, step.title), h('p', null, step.text)),
            h('span', { className: 'emap-step-number', 'aria-hidden': true }, step.number))))),
      h('section', { className: 'emap-facts', 'aria-label': 'About the assessment' },
        h('div', { className: 'emap-container emap-facts-grid' },
          facts.map(fact => h('div', { className: 'emap-fact', key: fact.title },
            h(Icon, { name: fact.icon }),
            h('div', null, h('h3', null, fact.title), h('p', null, fact.text)))))),
      h('section', { className: 'emap-next emap-container', 'aria-labelledby': 'emap-next-title' },
        h('div', { className: 'emap-next-copy' },
          h('p', { className: 'emap-eyebrow' }, 'Your next step starts with you'),
          h('h2', { id: 'emap-next-title' }, 'See your potential.', h('br'), 'Choose what comes next.'),
          h('p', null, 'Bring a fresh perspective to your learning, work and ideas.')),
        h('div', { className: 'emap-next-action' }, start('Explore your mindset', 'emap-button--light'),
          h('span', null, 'Reflect today. Build on it tomorrow.')))));
}

